import {
	faArrowLeft,
	faCartShopping,
	faCircleExclamation,
	faClipboardCheck,
	faCreditCard,
	faMinus,
	faPlus,
	faSpinner,
	faTag,
	faTimes,
	faTrash,
	faTriangleExclamation,
	faTruck,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	formatBillingDate,
	formatDateTime,
	formatPaymentMethodOptionLabel,
	PaymentMethodTypeEnum,
} from '@supply-mover-2-0/core';
import type {
	PaymentDecline,
	PortalCartDto,
	PortalCheckoutAddressDto,
	PortalCheckoutOptionsDto,
	PortalCheckoutPaymentMethodDto,
	PortalCheckoutPaymentTypeDto,
	PortalClientCreditDto,
	PortalShippingMethodDto,
	PricedCart,
	RecentMatchingOrderDto,
	ValidationIssue,
} from '@supply-mover-2-0/shared';
import {
	ACCOUNT_FUNDS_METHOD_TYPE as ACCOUNT_FUNDS_TYPE,
	customerPoLength,
	displayExternalId,
	formatPaymentRestrictionMessage,
	getCustomerPoLengthError,
	NO_PAYMENT_RESTRICTIONS,
} from '@supply-mover-2-0/shared';
import {
	CardBrandIcon,
	ConfirmDialog,
	extractPaymentDecline,
	ImagePlaceholder,
	PaymentDeclineModal,
	PromoChip,
	ResilientImage,
	Select,
	Toast,
	useTranslation,
} from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AccountPendingNotice } from '../components/AccountPendingNotice';
import { CartSuggestions } from '../components/CartSuggestions';
import { ItemDesignationBadges } from '../components/ItemDesignationBadges';
import { PortalAddPaymentMethod } from '../components/PortalAddPaymentMethod';
import { PortalSidebar } from '../components/PortalSidebar';
import { useDisplaySettings } from '../hooks/useDisplaySettings';
import { useIsKeyboardLikelyOpen } from '../hooks/useIsKeyboardLikelyOpen';
import { usePortalAuth } from '../hooks/usePortalAuth';
import { toCustomerDecline } from '../lib/customerDecline';
import { formatCurrency } from '../lib/formatCurrency';
import {
	classifyStorefrontSubmitFailure,
	confirmStorefrontPlacement,
} from '../lib/submitOutcome';
import * as cartService from '../services/portalCartService';
import { useCartStore } from '../stores/cartStore';

/**
 * PD-16731 — an order placed for the active account in the last 30 minutes
 * with the same items and quantities as the cart, or null. A failed check
 * never stops the order: it is a warning, not a gate.
 */
async function findRecentMatchingOrder(
	token: string,
): Promise<RecentMatchingOrderDto | null> {
	try {
		const response = await cartService.getRecentMatchingOrder(token);

		return response.success ? response.data : null;
	} catch {
		return null;
	}
}

/**
 * Combined cart + checkout page — matches v1.0 single-view pattern.
 *
 * Layout (top to bottom):
 * 1. Cart line items with quantity controls
 * 2. Shipping address selector
 * 3. Billing address selector
 * 4. Shipping method selector + disclaimer
 * 5. Payment method selection
 * 6. Customer PO field
 * 7. Credit info (if pay on account)
 * 8. Validation warnings/errors
 * 9. Order summary + Place Order button (sticky on mobile)
 *
 * All checkout options come from GET /cart/checkout-options.
 * Addresses are filtered by billtoOrShipto. Shipping address
 * changes trigger server-side recalculation.
 */
export function CartPage(): JSX.Element {
	const { t } = useTranslation();
	const navigate = useNavigate();
	// PD-14239: when hidePricing is on for the buyer's active client, the API
	// nulls all cart money fields and forces canSeePricing=false. Every money
	// render below is gated on this flag so no price/total leaks through.
	const display = useDisplaySettings();
	const { token } = usePortalAuth();
	const cart = useCartStore((s) => s.cart);
	const isLoading = useCartStore((s) => s.isLoading);
	const setCart = useCartStore((s) => s.setCart);
	const setLoading = useCartStore((s) => s.setLoading);
	// Total UNITS in the cart (sum of every line's quantityOrdered) — the exact
	// figure the header cart badge shows. Surfaced in the mobile pinned
	// Place-Order bar so the CTA carries qty + total like the header (PD-14095).
	const totalQuantity = useCartStore((s) => s.totalQuantity);

	// Checkout options from server
	const [checkoutOptions, setCheckoutOptions] =
		useState<PortalCheckoutOptionsDto | null>(null);
	const [checkoutLoading, setCheckoutLoading] = useState(false);

	// Form state
	const [shippingAddressId, setShippingAddressId] = useState<number | null>(
		null,
	);
	const [billingAddressId, setBillingAddressId] = useState<number | null>(null);
	const [shippingMethodId, setShippingMethodId] = useState<number | null>(null);
	const [paymentTypeId, setPaymentTypeId] = useState<number | null>(null);
	const [paymentMethodId, setPaymentMethodId] = useState<number | null>(null);
	const [customerPO, setCustomerPO] = useState('');

	// Add payment method sidebar
	const [showAddPaymentSidebar, setShowAddPaymentSidebar] = useState(false);

	// Submission + validation
	const [submitting, setSubmitting] = useState(false);
	const [submitError, setSubmitError] = useState<string | null>(null);
	// PD-15185 — a gateway decline, shown as a dialog rather than inline.
	const [decline, setDecline] = useState<PaymentDecline | null>(null);
	const [validationErrors, setValidationErrors] = useState<
		Array<ValidationIssue>
	>([]);
	const [validationWarnings, setValidationWarnings] = useState<
		Array<ValidationIssue>
	>([]);

	// Bumped after cart mutations settle to trigger re-validation
	const [validationVersion, setValidationVersion] = useState(0);
	const triggerRevalidation = useCallback(() => {
		setValidationVersion((v) => v + 1);
	}, []);

	// True while any quantity updates are queued or in-flight — disables Place Order
	const [hasPendingUpdates, setHasPendingUpdates] = useState(false);

	// Hide the mobile pinned Place-Order bar while the soft keyboard is up
	// (mirrors BottomNav) so the CTA never sits over the keyboard. PD-14095.
	const keyboardOpen = useIsKeyboardLikelyOpen();

	// Clear-cart confirmation modal
	const [showClearConfirm, setShowClearConfirm] = useState(false);
	// PD-16731 — the likely-duplicate question. Holds the resolver of the
	// promise handleSubmit is awaiting, so the page stays in its submitting
	// state until the buyer answers.
	const [recentMatchPrompt, setRecentMatchPrompt] = useState<{
		match: RecentMatchingOrderDto;
		resolve: (placeAnyway: boolean) => void;
	} | null>(null);
	const [clearing, setClearing] = useState(false);

	// Tracks the line item currently being removed so we can swap the trash
	// icon for a spinner — without this the row sits visually unchanged
	// while the network request is in flight, looking like the click did
	// nothing.
	const [removingId, setRemovingId] = useState<number | null>(null);

	// Fetch cart on mount
	useEffect(() => {
		if (token == null) return;
		setLoading(true);
		cartService
			.getCart(token)
			.then((res) => setCart(res.data))
			.catch(() => setCart(null))
			.finally(() => setLoading(false));
	}, [token, setCart, setLoading]);

	// Refetch the cart when a line item's image URL fails — the most
	// common cause is a stale presigned S3 URL whose backing session
	// aged out. Asking for a new cart yields freshly-signed URLs.
	const refreshCartForStaleImage = useCallback(() => {
		if (token == null) return;
		void cartService
			.getCart(token)
			.then((res) => setCart(res.data))
			.catch(() => {
				/* silent — image fallback handles the visual */
			});
	}, [token, setCart]);

	// Fetch checkout options once cart is loaded and has items
	const lineItems = cart?.lineItems ?? [];

	// Lookup map: orderDetailId → priced engine line so each rendered cart row
	// can show its own discount treatment (strikethrough original + final
	// price + a chip naming the promo). Without this the customer has to
	// scroll to the Order Summary to figure out why a line is cheaper or
	// which promo earned them the savings.
	const pricedLineByDetailId = new Map<
		number,
		{
			originalSubtotal: number;
			lineDiscountTotal: number;
			finalSubtotal: number;
			appliedPromos: Array<{ promoName: string; badgeText: string }>;
		}
	>();
	for (const pl of cart?.priced?.lines ?? []) {
		pricedLineByDetailId.set(pl.orderDetailId, {
			originalSubtotal: pl.originalSubtotal,
			lineDiscountTotal: pl.lineDiscountTotal,
			finalSubtotal: pl.finalSubtotal,
			appliedPromos: pl.appliedPromos ?? [],
		});
	}

	// Track whether initial checkout options have loaded so we can distinguish
	// between the first load (pre-fill everything) and subsequent refreshes
	// (only update shipping methods since item changes affect available methods).
	const initialCheckoutLoadedRef = useRef(false);

	/** Apply auto-selection logic for shipping methods based on available options */
	const autoSelectShippingMethod = useCallback(
		(opts: PortalCheckoutOptionsDto) => {
			if (!opts.isShippingConfigured || opts.shippingMethods.length === 0) {
				setShippingMethodId(null);
				return;
			}

			// If the currently selected method is still available and not disabled, keep it
			if (shippingMethodId != null) {
				const stillValid = opts.shippingMethods.find(
					(m) => m.id === shippingMethodId && !m.isDisabled,
				);
				if (stillValid != null) return;
			}

			// Auto-select the first available (non-disabled) method
			const firstAvailable = opts.shippingMethods.find((m) => !m.isDisabled);
			setShippingMethodId(firstAvailable?.id ?? null);
		},
		[shippingMethodId],
	);

	// Initial load — pre-fills all form fields from server state
	useEffect(() => {
		if (token == null || lineItems.length === 0) return;
		if (initialCheckoutLoadedRef.current) return;

		setCheckoutLoading(true);
		cartService
			.getCheckoutOptions(token)
			.then((res) => {
				if (res.success) {
					const opts = res.data;
					setCheckoutOptions(opts);
					initialCheckoutLoadedRef.current = true;

					// Pre-fill from cart state or defaults
					setShippingAddressId(
						opts.currentShippingAddressId ??
							opts.defaultShippingAddressId ??
							opts.shippingAddresses[0]?.id ??
							null,
					);
					setBillingAddressId(
						opts.currentBillingAddressId ??
							opts.defaultBillingAddressId ??
							opts.billingAddresses[0]?.id ??
							null,
					);

					// Pre-fill shipping method: current selection, or auto-select
					if (opts.currentShippingMethodId != null) {
						setShippingMethodId(opts.currentShippingMethodId);
					} else {
						autoSelectShippingMethod(opts);
					}

					setCustomerPO(opts.currentCustomerPO ?? '');

					// PD-16010 — the server decides which types checkout offers.
					// PD-16526 — it also decides which one checkout opens on: the
					// client's default payment method (an On Account default, or
					// the saved Primary) when offered, else On Account, else the
					// first type. Resolved server-side so it matches the default
					// CRM order entry uses; Account Funds is never chosen, since
					// it is the split-tender checkbox rather than a radio option.
					if (opts.defaultPaymentTypeId != null) {
						setPaymentTypeId(opts.defaultPaymentTypeId);
						setPaymentMethodId(opts.defaultPaymentMethodId);
					}
				}
			})
			.catch(() => {})
			.finally(() => setCheckoutLoading(false));
	}, [token, lineItems.length, autoSelectShippingMethod]);

	// Refresh checkout options after cart mutations (item add/remove/qty change).
	// Item changes affect available shipping methods because item requirements
	// (hazmat, refrigerated, heavy) determine which methods are required/disabled.
	useEffect(() => {
		if (token == null || !initialCheckoutLoadedRef.current) return;
		if (validationVersion === 0) return;

		cartService
			.getCheckoutOptions(token)
			.then((res) => {
				if (res.success) {
					setCheckoutOptions(res.data);
					autoSelectShippingMethod(res.data);
				}
			})
			.catch(() => {});
	}, [token, validationVersion, autoSelectShippingMethod]);

	// Pre-flight validation — runs on load and re-runs after every cart mutation.
	// A mutation bumps validationVersion, which refreshes the checkout options
	// above, and the new options re-run this. Listing validationVersion here as
	// well validated every change twice (PD-14466).
	useEffect(() => {
		if (token == null || checkoutOptions == null) return;
		cartService
			.validateCart(token)
			.then((res) => {
				if (res.success) {
					// PD-16936 — before submit, the PO box is what counts: the
					// submit replaces the cart's PO with it, and its own counter
					// holds Place Order while it is too long. The stored PO's
					// refusal would otherwise hold the button even after the
					// buyer shortens the text, since typing re-runs nothing.
					setValidationErrors(
						res.data.errors.filter(
							(issue) => issue.type !== 'customerPoTooLong',
						),
					);
					setValidationWarnings(res.data.warnings);
				}
			})
			.catch(() => {});
	}, [token, checkoutOptions]);

	// After adding a payment method, refresh checkout options so the new method
	// appears in the saved methods list and auto-select it.
	const handlePaymentMethodAdded = useCallback(async () => {
		setShowAddPaymentSidebar(false);
		if (token == null) return;

		try {
			const res = await cartService.getCheckoutOptions(token);
			if (res.success) {
				setCheckoutOptions(res.data);

				// Auto-select the newest saved method for the currently selected payment type
				const currentType = res.data.paymentTypes.find(
					(pt) => pt.id === paymentTypeId,
				);
				if (currentType != null) {
					const methodsForType = res.data.savedPaymentMethods.filter(
						(m) => m.type === currentType.paymentMethodType,
					);
					if (methodsForType.length > 0) {
						// Select the last one added (highest ID)
						const newest = methodsForType.reduce((a, b) =>
							a.id > b.id ? a : b,
						);
						setPaymentMethodId(newest.id);
					}
				}
			}
		} catch {
			// Non-critical — options will refresh on next page load
		}
	}, [token, paymentTypeId]);

	// --- Handlers ---

	// Aggregate debounced quantity updates — collects all pending changes across
	// items and flushes them together after 500ms of inactivity. Prevents rapid
	// edits on multiple items from firing separate racing API calls.
	const pendingQuantities = useRef<Record<number, number>>({});
	const aggregateTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

	const flushAllPendingUpdates = useCallback(async () => {
		const pending = { ...pendingQuantities.current };
		pendingQuantities.current = {};
		aggregateTimer.current = null;

		const entries = Object.entries(pending);
		if (entries.length === 0 || token == null) {
			setHasPendingUpdates(false);
			return;
		}

		try {
			// PD-14466 — every pending change goes in ONE request. The server
			// recalculates the cart once and returns it once, where one request
			// per line rebuilt (and re-priced) the cart per line and raced the
			// other requests' total writes.
			const response = await cartService.applyCartItemChanges(token, {
				operations: entries.map(([detailId, qty]) => ({
					type: 'update' as const,
					detailId: Number(detailId),
					quantityOrdered: qty,
				})),
			});
			if (response.data != null) setCart(response.data);
			triggerRevalidation();
			// PD-16171 — say why a change was refused (e.g. raising the quantity
			// of a line whose item has no price) instead of silently snapping
			// the quantity back. The returned cart already shows the line as
			// the server kept it.
			const refused = response.results.find((result) => !result.success);
			if (refused != null) {
				Toast.show({
					type: 'error',
					text1: t('Could not update cart'),
					text2: refused.error != null ? t(refused.error) : undefined,
				});
			}
		} catch (error) {
			// PD-16171 — say why the change was refused (e.g. raising the
			// quantity of a line whose item has no price) instead of silently
			// snapping the quantity back, as the catalog and product steppers do.
			Toast.show({
				type: 'error',
				text1: t('Could not update cart'),
				text2: error instanceof Error ? t(error.message) : undefined,
			});
			// On error, refetch to get accurate server state
			try {
				const res = await cartService.getCart(token);
				setCart(res.data);
			} catch {
				/* non-critical */
			}
		} finally {
			setHasPendingUpdates(false);
		}
	}, [token, setCart, triggerRevalidation, t]);

	const handleUpdateQuantity = useCallback(
		(detailId: number, quantityOrdered: number) => {
			if (quantityOrdered < 1) return;

			pendingQuantities.current[detailId] = quantityOrdered;
			setHasPendingUpdates(true);

			// Optimistic update — immediately reflect in the cart store
			const currentCart = useCartStore.getState().cart;
			if (currentCart != null) {
				const updatedItems = currentCart.lineItems.map((item) => {
					if (item.id === detailId) {
						// PD-16171 — a line with no price keeps its subtotal rather
						// than being re-costed at an assumed $0.
						const price = item.price != null ? parseFloat(item.price) : NaN;
						return {
							...item,
							quantityOrdered,
							subtotal: Number.isFinite(price)
								? (price * quantityOrdered).toFixed(2)
								: item.subtotal,
						};
					}
					return item;
				});
				const newSubtotal = updatedItems.reduce(
					(sum, item) => sum + parseFloat(item.subtotal ?? '0'),
					0,
				);
				setCart({
					...currentCart,
					lineItems: updatedItems,
					subtotal: newSubtotal.toFixed(2),
					total: newSubtotal.toFixed(2),
				});
			}

			// Reset the shared debounce timer — waits for 500ms of inactivity
			// across ALL items before flushing everything together
			if (aggregateTimer.current != null) {
				clearTimeout(aggregateTimer.current);
			}
			aggregateTimer.current = setTimeout(() => {
				void flushAllPendingUpdates();
			}, 500);
		},
		[setCart, flushAllPendingUpdates],
	);

	const handleRemoveItem = useCallback(
		async (detailId: number) => {
			if (token == null) return;
			setRemovingId(detailId);
			try {
				const res = await cartService.removeFromCart(token, detailId);
				setCart(res.data);
				triggerRevalidation();
			} catch {
			} finally {
				setRemovingId(null);
			}
		},
		[token, setCart, triggerRevalidation],
	);

	const handleClearCart = useCallback(async () => {
		if (token == null) return;
		setClearing(true);
		try {
			await cartService.clearCart(token);
			setCart(null);
			setShowClearConfirm(false);
		} catch {
			// Leave dialog open so user can retry or cancel
		} finally {
			setClearing(false);
		}
	}, [token, setCart]);

	const handleShippingAddressChange = useCallback(
		async (newId: number) => {
			setShippingAddressId(newId);
			if (token == null || billingAddressId == null) return;
			try {
				const res = await cartService.updateAddresses(token, {
					billingAddressId,
					shippingAddressId: newId,
				});
				if (res.success) {
					setCart(res.data);
					triggerRevalidation();
				}
			} catch {}
		},
		[token, billingAddressId, setCart, triggerRevalidation],
	);

	const handleBillingAddressChange = useCallback(
		async (newId: number) => {
			setBillingAddressId(newId);
			if (token == null || shippingAddressId == null) return;
			try {
				const res = await cartService.updateAddresses(token, {
					billingAddressId: newId,
					shippingAddressId,
				});
				if (res.success) {
					setCart(res.data);
					triggerRevalidation();
				}
			} catch {}
		},
		[token, shippingAddressId, setCart, triggerRevalidation],
	);

	// Portal checkout: Credit Card, ACH, and Company Credit. Account Funds is
	// no longer a radio option — any positive balance is offered through the
	// split-tender checkbox below (PD-15084): funds apply first, up to the
	// cart total, and the selected method collects only the remainder (no
	// method needed when the balance covers everything).
	// PD-15869 — a cycle-billed account does not choose how to pay at
	// checkout: nothing is collected now, and the resulting invoice joins
	// the account's next cycle charge, taken with the account's own method.
	// Offering a card here would promise a charge that never happens, so
	// the picker is locked to On Account.
	const isCycleBilling = checkoutOptions?.billing?.mode === 'cycle_billing';

	const ecommercePaymentTypes = useMemo(() => {
		// PD-16010 — server-filtered; see the note in the load effect above.
		// Account Funds is excluded because it is the split-tender checkbox,
		// not a radio option.
		const offered = (checkoutOptions?.paymentTypes ?? []).filter(
			(pt) => pt.paymentMethodType !== ACCOUNT_FUNDS_TYPE,
		);
		if (!isCycleBilling) {
			return offered;
		}
		const onAccount = offered.filter(
			(pt) => pt.paymentMethodType === 'Company Credit',
		);
		// Defensive: if the tenant has removed Company Credit from this
		// client's allowed types the checkout still has to work, so fall
		// back to the normal list rather than rendering nothing.
		return onAccount.length > 0 ? onAccount : offered;
	}, [checkoutOptions, isCycleBilling]);

	/**
	 * PD-15869 — the one line that replaces the net-terms helper for a
	 * cycle-billed account: when the charge happens, and whose account it
	 * lands on when a parent pays.
	 */
	const cycleBillingHelper = useMemo((): string | null => {
		const billing = checkoutOptions?.billing;
		if (billing == null || billing.mode !== 'cycle_billing') {
			return null;
		}
		const date = formatBillingDate(billing.cycleNextDate);
		if (billing.billedToName != null) {
			return date != null
				? t('Billed to {{account}} on their next cycle ({{date}})', {
						account: t(billing.billedToName),
						date: t(date),
					})
				: t('Billed to {{account}} on their next cycle', {
						account: t(billing.billedToName),
					});
		}
		return date != null
			? t('Billed on your next cycle ({{date}})', { date: t(date) })
			: t('Billed on your next cycle');
	}, [checkoutOptions, t]);

	// PD-15084 — split-tender state. The server recomputes the applied
	// amount from the live balance; these figures are display-only.
	const [useAccountFunds, setUseAccountFunds] = useState(false);
	// PD-16900 — the buyer chose to have their card charged when the order is
	// placed rather than at delivery or on terms. Offered only where checkout
	// says so (billing modes on, not cycle billing) and only for a card.
	const [payAtOrder, setPayAtOrder] = useState(false);
	const accountFundsAvailable = checkoutOptions?.accountFunds?.available ?? 0;
	const accountFundsType = useMemo(
		() =>
			(checkoutOptions?.paymentTypes ?? []).find(
				(pt) => pt.paymentMethodType === ACCOUNT_FUNDS_TYPE,
			) ?? null,
		[checkoutOptions],
	);

	const selectedPaymentType = useMemo(
		() => ecommercePaymentTypes.find((pt) => pt.id === paymentTypeId) ?? null,
		[ecommercePaymentTypes, paymentTypeId],
	);

	// PD-15869 — hold the selection on the locked On Account option. The
	// initial pre-fill runs before `billing` is known to this component, and
	// a cart refresh can re-offer a card, so this corrects both.
	useEffect(() => {
		if (!isCycleBilling) {
			return;
		}
		const onAccount = ecommercePaymentTypes.find(
			(pt) => pt.paymentMethodType === 'Company Credit',
		);
		if (onAccount != null && paymentTypeId !== onAccount.id) {
			setPaymentTypeId(onAccount.id);
			setPaymentMethodId(null);
		}
	}, [isCycleBilling, ecommercePaymentTypes, paymentTypeId]);

	// --- Shipping method derived state ---

	const selectedShippingMethod = useMemo(
		() =>
			checkoutOptions?.shippingMethods.find((m) => m.id === shippingMethodId) ??
			null,
		[checkoutOptions?.shippingMethods, shippingMethodId],
	);

	/** Compute effective shipping price based on fee, threshold, and current subtotal */
	const computeShippingEffectivePrice = useCallback(
		(method: PortalShippingMethodDto): string => {
			// PD-14239: method.fee is null for hide-pricing buyers (the shipping
			// cost is hidden); treat as 0 for the display-only preview math.
			const fee = parseFloat(method.fee ?? '0') || 0;
			const threshold =
				method.freeShippingThreshold != null
					? parseFloat(method.freeShippingThreshold)
					: null;
			// PD-15795: a method can exclude items from its own threshold, so
			// prefer the basis the server actually compared (`thresholdSubtotal`)
			// over the raw cart subtotal. Null for hide-pricing buyers, and
			// absent on an older response — fall back to the cart subtotal then.
			const parsedThresholdSubtotal =
				method.thresholdSubtotal != null
					? parseFloat(method.thresholdSubtotal)
					: NaN;
			const subtotal = Number.isFinite(parsedThresholdSubtotal)
				? parsedThresholdSubtotal
				: parseFloat(cart?.subtotal ?? '0');

			if (threshold != null && !isNaN(threshold) && subtotal >= threshold) {
				return '0.00';
			}

			return fee.toFixed(2);
		},
		[cart?.subtotal],
	);

	// Shipping fee for the selected method (display-only, server calculates actual).
	// Includes the additional surcharge from client/item allowedShippingMethods if present.
	const shippingFeePreview = useMemo(() => {
		if (selectedShippingMethod == null) return 0;
		const basePrice = parseFloat(
			computeShippingEffectivePrice(selectedShippingMethod),
		);
		const surcharge =
			selectedShippingMethod.additionalFee != null
				? parseFloat(selectedShippingMethod.additionalFee)
				: 0;
		return basePrice + surcharge;
	}, [selectedShippingMethod, computeShippingEffectivePrice]);

	// Calculate fee preview based on selected payment type.
	// The server applies the actual fee at submission — this is display only.
	const feePreview = useMemo(() => {
		// PD-14239: processingFee is null for hide-pricing buyers (fees hidden);
		// treat as 0 so this display-only preview degrades cleanly.
		const processingFee = selectedPaymentType?.processingFee ?? 0;
		if (processingFee <= 0) {
			return 0;
		}

		// PD-15142 — the fee configured against a payment-method TYPE describes
		// what a CREDIT card would cost. Only a credit card may be surcharged:
		// Regulation II prohibits surcharging debit, including signature debit
		// run as credit, and the networks extend that to prepaid. The server
		// takes no fee on those, so quoting one here would show a total the
		// customer is never charged. A card not yet classified is quoted
		// fee-free for the same reason.
		const selectedCard = checkoutOptions?.savedPaymentMethods.find(
			(method) => method.id === paymentMethodId,
		);
		if (selectedCard != null && !selectedCard.isSurchargeable) {
			return 0;
		}
		const subtotal = parseFloat(cart?.subtotal ?? '0');
		// PD-15084 — a processing fee follows the form of payment: it applies
		// to the portion paid with that type. Account funds carry no fee, so
		// when they cover part of the order the selected method's rate is
		// charged only on the share it actually collects.
		const fundsApplied = useAccountFunds
			? Math.min(accountFundsAvailable, subtotal)
			: 0;
		const feeBase = Math.max(0, subtotal - fundsApplied);
		if (feeBase <= 0.005) {
			return 0;
		}
		if (selectedPaymentType?.processingFeeType === '%') {
			return feeBase * (processingFee / 100);
		}
		return processingFee;
	}, [
		selectedPaymentType,
		paymentMethodId,
		checkoutOptions?.savedPaymentMethods,
		cart?.subtotal,
		useAccountFunds,
		accountFundsAvailable,
	]);

	// Calculate the display total including shipping fee and processing fee previews,
	// less any promo savings the engine has computed for this cart preview.
	const promoSavings = cart?.priced?.totalSavings ?? 0;
	const preFeeDisplayTotal = useMemo(() => {
		const subtotal = parseFloat(cart?.subtotal ?? '0');
		const freight = parseFloat(cart?.freight ?? '0');
		const tax = parseFloat(cart?.tax ?? '0');
		return subtotal + freight + tax + shippingFeePreview - promoSavings;
	}, [
		cart?.subtotal,
		cart?.freight,
		cart?.tax,
		shippingFeePreview,
		promoSavings,
	]);

	// PD-15084 — full cover is judged against the pre-fee total: Account
	// Funds carries no processing fee, so a balance that covers the goods
	// covers the order even though the selected card type would have added
	// a fee. The server recomputes this authoritatively at submit.
	const fundsCoverEverything =
		useAccountFunds && accountFundsAvailable >= preFeeDisplayTotal - 0.005;

	// PD-15585 — a balance LARGER than the order would be spent only in part,
	// which the tenant's ERP may be unable to apply. Stated under the
	// checkbox, with the tenant's wording, and Place Order waits; a balance
	// the order can absorb (the split above) is consumed whole and unaffected.
	// The server refuses the same shape.
	const partialFundsRestriction =
		checkoutOptions?.paymentRestrictions?.partialAccountFundsSpend ??
		NO_PAYMENT_RESTRICTIONS.partialAccountFundsSpend;
	const partialFundsBlocked =
		partialFundsRestriction.blocked &&
		useAccountFunds &&
		accountFundsAvailable > preFeeDisplayTotal + 0.005;
	const partialFundsMessage = formatPaymentRestrictionMessage(
		partialFundsRestriction.message,
		formatCurrency(accountFundsAvailable) ?? '',
	);

	// feePreview is already zero when account funds cover the whole order —
	// the selected method collects nothing, so it charges no fee.
	const displayTotal = useMemo(
		() => preFeeDisplayTotal + feePreview,
		[preFeeDisplayTotal, feePreview],
	);

	// Filter validation messages based on payment context.
	// Credit-related issues only matter when paying on account.
	const creditValidationTypes = new Set(['creditLimit', 'creditHold']);
	const isPayOnAccount =
		selectedPaymentType?.paymentMethodType === 'Company Credit';
	const cardPaymentTiming = checkoutOptions?.cardPaymentTiming ?? null;
	const isCardSelected =
		selectedPaymentType?.paymentMethodType === 'Credit Card';
	const canChoosePayAtOrder =
		cardPaymentTiming?.canChoosePayAtOrder === true && isCardSelected;
	const accountPaysAtOrder =
		cardPaymentTiming?.mode === 'pay_at_order' && isCardSelected;

	const filteredErrors = useMemo(
		() =>
			isPayOnAccount
				? validationErrors
				: validationErrors.filter((e) => !creditValidationTypes.has(e.type)),
		[validationErrors, isPayOnAccount],
	);

	const filteredWarnings = useMemo(
		() =>
			isPayOnAccount
				? validationWarnings
				: validationWarnings.filter((w) => !creditValidationTypes.has(w.type)),
		[validationWarnings, isPayOnAccount],
	);

	// Split into cart-level (no itemId) and item-level (has itemId) for display.
	// Cart-level issues show in the top alert box. Item-level issues render inline on each item.
	const cartLevelErrors = useMemo(
		() => filteredErrors.filter((e) => e.itemId == null),
		[filteredErrors],
	);
	const cartLevelWarnings = useMemo(
		() => filteredWarnings.filter((w) => w.itemId == null),
		[filteredWarnings],
	);
	const itemErrorMap = useMemo(() => {
		// Key type mirrors the nullable line-item fkItemId so lookups below
		// typecheck; only non-null ids are ever inserted.
		const map = new Map<number | null, Array<ValidationIssue>>();
		for (const err of filteredErrors) {
			if (err.itemId != null) {
				const existing = map.get(err.itemId) ?? [];
				existing.push(err);
				map.set(err.itemId, existing);
			}
		}
		return map;
	}, [filteredErrors]);
	const itemWarningMap = useMemo(() => {
		// Same nullable key type as itemErrorMap above.
		const map = new Map<number | null, Array<ValidationIssue>>();
		for (const warn of filteredWarnings) {
			if (warn.itemId != null) {
				const existing = map.get(warn.itemId) ?? [];
				existing.push(warn);
				map.set(warn.itemId, existing);
			}
		}
		return map;
	}, [filteredWarnings]);

	// PD-16936 — the tenant's Customer PO limit (null for none). Judged on the
	// trimmed text because that is what the submit sends, and with no stored
	// value: the order checks refuse an over-limit PO already on the cart too.
	const customerPoMaxLength = checkoutOptions?.customerPoMaxLength ?? null;
	const trimmedCustomerPO = customerPO.trim();
	const customerPoError =
		customerPoMaxLength != null &&
		getCustomerPoLengthError(trimmedCustomerPO, customerPoMaxLength) != null
			? t('Customer PO cannot exceed {{max}} characters.', {
					max: customerPoMaxLength,
				})
			: null;

	const canSubmit = useMemo(() => {
		if (shippingAddressId == null || billingAddressId == null) return false;
		if (paymentTypeId == null && !fundsCoverEverything) {
			return false;
		}
		// Require a shipping method selection when shipping options are available
		const hasShippingOptions = (checkoutOptions?.shippingMethods ?? []).some(
			(m) => !m.isDisabled,
		);
		if (hasShippingOptions && shippingMethodId == null) return false;
		const needsSavedMethod =
			!fundsCoverEverything &&
			selectedPaymentType != null &&
			['Credit Card', 'ACH'].includes(selectedPaymentType.paymentMethodType);
		if (needsSavedMethod && paymentMethodId == null) return false;
		if (filteredErrors.length > 0) return false;
		if (partialFundsBlocked) {
			return false;
		}
		// PD-13362 — a lead's account is still being set up: the cart and
		// checkout stay usable, only Place Order waits (the API refuses it too).
		if (display.isAccountPending) return false;
		// PD-16936 — the PO is never shortened for the buyer; they shorten it.
		if (customerPoError != null) return false;
		return true;
	}, [
		shippingAddressId,
		billingAddressId,
		paymentTypeId,
		paymentMethodId,
		selectedPaymentType,
		filteredErrors,
		checkoutOptions?.shippingMethods,
		shippingMethodId,
		fundsCoverEverything,
		partialFundsBlocked,
		display.isAccountPending,
		customerPoError,
	]);

	const handleSubmit = useCallback(async () => {
		if (
			token == null ||
			!canSubmit ||
			shippingAddressId == null ||
			billingAddressId == null
		)
			return;
		setSubmitting(true);
		setSubmitError(null);
		const cartId = cart?.id ?? null;

		try {
			// PD-16731 — ask before placing a cart that matches an order just
			// placed for this account. The buyer can still place it.
			const recentMatch = await findRecentMatchingOrder(token);
			if (recentMatch != null) {
				const placeAnyway = await new Promise<boolean>((resolve) => {
					setRecentMatchPrompt({ match: recentMatch, resolve });
				});
				if (!placeAnyway) {
					return;
				}
			}

			// PD-15084 — full cover submits as a plain Account Funds payment;
			// a partial balance rides the applyAccountFunds flag alongside the
			// selected method, which then collects only the remainder.
			const paymentFields = fundsCoverEverything
				? {
						paymentMethodTypeId: accountFundsType?.id,
						paymentMethodType: accountFundsType?.paymentMethodType,
					}
				: {
						paymentMethodId: paymentMethodId ?? undefined,
						paymentMethodTypeId: paymentTypeId ?? undefined,
						paymentMethodType: selectedPaymentType?.paymentMethodType,
						...(useAccountFunds && { applyAccountFunds: true }),
						...(payAtOrder && canChoosePayAtOrder && { payAtOrder: true }),
					};

			const res = await cartService.submitCart(token, {
				billingAddressId,
				shippingAddressId,
				shippingMethodId: shippingMethodId ?? undefined,
				...paymentFields,
				customerPO:
					customerPO.trim().length > 0 ? customerPO.trim() : undefined,
			});

			if (res.success && res.data != null) {
				setCart(null);
				navigate(`/orders/${res.data.orderId}`);
			} else if (res.errors != null && res.errors.length > 0) {
				// PD-15185 — a refused payment comes back as a validation issue
				// rather than a thrown error, with the structured decline on the
				// issue's `data`. Everything else stays in the inline list.
				const paymentIssue = res.errors.find(
					(issue) => issue.type === 'paymentFailed' && issue.data != null,
				);
				// PD-16016 — re-voiced for the customer like every other storefront
				// decline; the issue's own message is the staff-facing sentence, so
				// the inline list shows the same customer wording as the dialog.
				const paymentDecline = toCustomerDecline(
					(paymentIssue?.data as PaymentDecline | undefined) ?? null,
				);

				if (paymentDecline != null) {
					setDecline(paymentDecline);
				}

				// PD-16845 — refused over the buyer's approval limit (the fee
				// chosen at checkout can take it over): reload the cart so the
				// page offers Request approval instead of Place Order.
				if (res.errors.some((issue) => issue.type === 'approvalLimitExceeded')) {
					void cartService
						.getCart(token)
						.then((refreshed) => setCart(refreshed.data))
						.catch(() => {
							/* the inline error still explains the refusal */
						});
				}

				setValidationErrors(
					paymentDecline == null
						? res.errors
						: res.errors.map((issue) =>
								issue === paymentIssue
									? { ...issue, message: paymentDecline.message }
									: issue,
							),
				);
			}
		} catch (error) {
			const paymentDecline = toCustomerDecline(extractPaymentDecline(error));

			if (paymentDecline != null) {
				setDecline(paymentDecline);
			} else if (
				cartId != null &&
				classifyStorefrontSubmitFailure(error) === 'unknown'
			) {
				// PD-16731 — a gateway timeout, a dropped connection or another
				// submit still running: the order may have been placed, or may
				// still be being placed. Keep checking while the submit runs
				// before saying anything, and never show the raw
				// "Request failed with status 504".
				const placement = await confirmStorefrontPlacement({
					fetchStatus: async () =>
						(await cartService.getSubmitStatus(token, cartId)).data,
				});

				if (placement === 'placed') {
					setCart(null);
					navigate(`/orders/${cartId}`);
				} else {
					setSubmitError(
						t(
							'We could not confirm whether your order was placed. Check your orders before trying again.',
						),
					);
				}
			} else {
				setSubmitError((error as Error).message ?? t('Failed to place order.'));
			}
		} finally {
			setSubmitting(false);
		}
	}, [
		cart,
		token,
		canSubmit,
		shippingAddressId,
		billingAddressId,
		shippingMethodId,
		paymentMethodId,
		paymentTypeId,
		selectedPaymentType,
		fundsCoverEverything,
		accountFundsType,
		useAccountFunds,
		customerPO,
		setCart,
		navigate,
		t,
	]);

	// PD-16845 — an order over the buyer's own approval limit cannot be
	// placed by them. The page offers "Request approval" instead of Place
	// Order, which emails and notifies the account's buyers who can approve
	// it; the cart stays here until one of them places it. The server refuses
	// an over-limit submit regardless.
	const approval = cart?.approval ?? null;
	// A lead account cannot place orders at all (PD-13362), so it never asks
	// for approval either; the account-pending notice explains the disabled button.
	const needsApproval =
		approval?.overLimit === true && !display.isAccountPending;
	const myApprovalRequest = approval?.request?.isMine === true;
	const approvalRequestedBy =
		approval?.request != null && !approval.request.isMine
			? (approval.request.requestedByName ?? t('Another buyer'))
			: null;
	const [requestingApproval, setRequestingApproval] = useState(false);
	// Lines and checks that would stop an approver placing the cart stop the
	// request too; the approval-limit refusal itself is what it answers.
	const canRequestApproval =
		needsApproval &&
		!requestingApproval &&
		!hasPendingUpdates &&
		lineItems.every((line) => line.isPriced) &&
		filteredErrors.every((issue) => issue.type === 'approvalLimitExceeded');

	const handleRequestApproval = useCallback(async () => {
		if (token == null) return;
		setRequestingApproval(true);
		setSubmitError(null);

		try {
			const res = await cartService.requestApproval(token);
			Toast.show({
				type: 'success',
				text1: t('Approval requested'),
				text2:
					res.data.recipientCount === 1
						? t('We emailed the buyer on this account who can approve it.')
						: t(
								'We emailed the {{count}} buyers on this account who can approve it.',
								{ count: res.data.recipientCount },
							),
			});
			setValidationErrors((issues) =>
				issues.filter((issue) => issue.type !== 'approvalLimitExceeded'),
			);
			const refreshed = await cartService.getCart(token);
			setCart(refreshed.data);
		} catch (error) {
			setSubmitError(
				error instanceof Error
					? error.message
					: t('Could not request approval. Please try again.'),
			);
		} finally {
			setRequestingApproval(false);
		}
	}, [token, setCart, t]);

	const requestApprovalLabel = requestingApproval
		? t('Requesting approval...')
		: myApprovalRequest
			? t('Resend approval request')
			: t('Request approval');

	// --- Render ---

	if (isLoading) {
		return (
			<div className="px-4 py-12 text-center">
				<FontAwesomeIcon
					icon={faSpinner}
					className="animate-spin text-2xl text-faint mb-3"
				/>
				<p className="text-sm text-brand-muted">{t('Loading cart...')}</p>
			</div>
		);
	}

	if (lineItems.length === 0) {
		return (
			<div className="px-4 py-12 text-center max-w-md mx-auto">
				<FontAwesomeIcon
					icon={faCartShopping}
					className="text-4xl text-disabled mb-4"
				/>
				<h1 className="text-lg font-bold text-default mb-2">
					{t('Your cart is empty')}
				</h1>
				<p className="text-sm text-brand-muted mb-6">
					{t('Browse our catalog to find products.')}
				</p>
				<button
					type="button"
					onClick={() => navigate('/catalog')}
					className="px-6 py-3 bg-portal-accent hover:bg-portal-accent-hover text-white font-medium rounded-lg transition-colors min-h-touch"
				>
					{t('Start Shopping')}
				</button>
			</div>
		);
	}

	return (
		// PD-13362 — extra bottom room on mobile while the pinned bar also
		// carries the account-pending notice, so the page foot is not hidden.
		<div
			className={`px-4 py-4 max-w-6xl mx-auto ${
				display.isAccountPending ? 'pb-64' : 'pb-40'
			} md:pb-8`}
		>
			<div className="flex items-center justify-between gap-3 mb-4">
				<button
					type="button"
					onClick={() => navigate('/catalog')}
					className="flex items-center gap-1.5 text-sm text-muted hover:text-default transition-colors min-h-touch"
				>
					<FontAwesomeIcon icon={faArrowLeft} className="text-xs" />
					<span>{t('Continue Shopping')}</span>
				</button>
				<button
					type="button"
					onClick={() => setShowClearConfirm(true)}
					className="flex items-center gap-1.5 text-sm text-danger hover:text-danger hover:underline transition-colors min-h-touch"
				>
					<FontAwesomeIcon icon={faTrash} className="text-xs" />
					<span>{t('Clear Cart')}</span>
				</button>
			</div>
			<h1 className="text-lg md:text-xl font-bold text-default mb-4">
				{t('Cart')} ({lineItems.length})
			</h1>

			{/* PD-16845 — who asked for this cart to be approved, and why this
			    buyer cannot place it themselves. */}
			{approvalRequestedBy != null && (
				<div className="mb-4 p-3 rounded-xl text-sm bg-portal-accent/10 border border-portal-accent/30 text-default">
					<div className="flex items-start gap-2">
						<FontAwesomeIcon
							icon={faClipboardCheck}
							className="text-portal-accent mt-0.5"
						/>
						<div>
							<p className="font-medium">
								{t('{{name}} asked you to approve this order.', {
									name: t(approvalRequestedBy),
								})}
							</p>
							<p className="mt-1 text-brand-muted">
								{needsApproval
									? t(
											'It is over your approval limit too, so you can only send it on for approval.',
										)
									: t(
											'Review the cart, choose shipping and payment, then place the order.',
										)}
							</p>
						</div>
					</div>
				</div>
			)}
			{needsApproval && (
				<div className="mb-4 p-3 rounded-xl text-sm bg-warning-badge text-warning-tint-fg border border-warning-tint">
					<div className="flex items-start gap-2">
						<FontAwesomeIcon
							icon={faTriangleExclamation}
							className="text-warning mt-0.5"
						/>
						<div>
							<p className="font-medium">
								{myApprovalRequest
									? t('Approval requested')
									: approval?.limit != null && display.canSeePricing
										? t('This order is over your approval limit of {{limit}}.', {
												limit: formatCurrency(approval.limit) ?? '',
											})
										: t('This order is over your approval limit.')}
							</p>
							<p className="mt-1">
								{myApprovalRequest
									? t(
											'The buyers on this account who can approve it have been emailed. One of them will place it, and it stays in this cart until then.',
										)
									: t(
											'Request approval and the buyers on this account who can approve it will be emailed to review and place it.',
										)}
							</p>
						</div>
					</div>
				</div>
			)}

			{/* Two-column on tablet/desktop (md+): line items + checkout sections on
			    the left, Order Summary + Place Order in a sticky right sidebar. Below
			    md it collapses to one column (the full summary stacks inline at the
			    foot) plus the slim pinned Place-Order bar rendered after this grid.
			    Default align-items:stretch is intentional — it lets the right grid
			    item grow to the row height so the inner md:sticky card has travel. */}
			<div className="md:grid md:grid-cols-[1fr_360px] md:gap-6">
				{/* LEFT COLUMN — items, suggestions, checkout sections, coupons */}
				<div className="min-w-0">
					{/* ---- CART ITEMS ---- */}
					<div className="space-y-3 mb-6">
						{lineItems.map((item) => {
							const itemErrors = itemErrorMap.get(item.fkItemId) ?? [];
							const itemWarnings = itemWarningMap.get(item.fkItemId) ?? [];
							const hasItemErrors = itemErrors.length > 0;
							const hasItemWarnings = itemWarnings.length > 0;

							return (
								<div
									key={item.id}
									className={`flex flex-wrap gap-3 bg-brand-surface border rounded-xl p-3 ${
										hasItemErrors
											? 'border-danger-tint'
											: hasItemWarnings
												? 'border-warning-tint'
												: 'border-brand-border'
									}`}
								>
									<div
										className="w-16 h-16 shrink-0 bg-brand-input rounded-lg flex items-center justify-center overflow-hidden cursor-pointer"
										onClick={() => navigate(`/product/${item.fkItemId}`)}
									>
										<ResilientImage
											src={item.primaryImageUrl}
											alt={t(item.description ?? '')}
											className="w-full h-full object-cover"
											onLikelyStaleUrl={refreshCartForStaleImage}
											fallback={<ImagePlaceholder size="sm" />}
										/>
									</div>
									<div className="flex-1 min-w-0">
										<h3
											className="text-sm font-medium text-default truncate cursor-pointer hover:text-portal-accent"
											onClick={() => navigate(`/product/${item.fkItemId}`)}
										>
											{t(item.description ?? '')}
										</h3>
										<p className="text-xs text-brand-muted">
											{t(item.itemExternalId)}
										</p>
										<ItemDesignationBadges designations={item.designations} />
										{(() => {
											const pl = pricedLineByDetailId.get(item.id);
											const promos = pl?.appliedPromos ?? [];
											if (promos.length === 0) return null;
											// Promo-name chip(s) under the row's metadata. The chip
											// names the campaign that produced the savings; the
											// strikethrough on the right names the savings amount.
											// Multi-promo lines stack chips horizontally with wrap.
											// Migrated to the unified PromoChip — same visual as
											// the catalog/detail/order surfaces.
											return (
												<div className="mt-1 flex flex-wrap gap-1">
													{promos.map((p) => (
														<PromoChip
															key={p.promoName}
															variant="inline"
															name={t(p.promoName)}
														/>
													))}
												</div>
											);
										})()}
										{item.fkItemId != null &&
											checkoutOptions?.itemSurcharges?.[item.fkItemId] !=
												null && (
												<p className="text-xs text-warning mt-0.5">
													{t(checkoutOptions.itemSurcharges[item.fkItemId])}
												</p>
											)}
										{/* Connected pill stepper — matches the CRM
										    StyledQuantitySelector: one bordered container, no
										    gaps, borderless transparent center input. End
										    buttons are square (w-9 h-9) so the icon's inset to
										    the rounded edge equals the top/bottom inset — the
										    end-padding gripe Chris raised on the CRM one. At
										    qty 1 the left control becomes a trash that removes
										    the line (CRM behaviour), so there's no separate
										    remove button on the row. */}
										<div className="inline-flex items-center mt-2 rounded-lg border border-brand-border bg-brand-surface overflow-hidden">
											<button
												type="button"
												onClick={() => {
													if (item.quantityOrdered <= 1) {
														void handleRemoveItem(item.id);
													} else {
														handleUpdateQuantity(
															item.id,
															item.quantityOrdered - 1,
														);
													}
												}}
												disabled={removingId === item.id}
												className="w-9 h-9 flex items-center justify-center text-muted hover:bg-black/5 dark:hover:bg-white/10 transition-colors disabled:opacity-40"
												aria-label={
													item.quantityOrdered <= 1
														? t('Remove item')
														: t('Decrease quantity')
												}
											>
												<FontAwesomeIcon
													icon={
														removingId === item.id
															? faSpinner
															: item.quantityOrdered <= 1
																? faTrash
																: faMinus
													}
													className="text-xs"
													spin={removingId === item.id}
												/>
											</button>
											<input
												type="number"
												min={1}
												value={item.quantityOrdered}
												onChange={(e) => {
													const val = parseInt(e.target.value, 10);
													if (!isNaN(val) && val >= 1) {
														handleUpdateQuantity(item.id, val);
													}
												}}
												className="w-12 h-9 !min-h-0 text-center !text-sm font-medium !border-0 !bg-transparent !shadow-none !rounded-none text-default outline-none focus:outline-none focus:!ring-0 focus:!shadow-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
												aria-label={t('Quantity')}
											/>
											<button
												type="button"
												onClick={() =>
													handleUpdateQuantity(
														item.id,
														item.quantityOrdered + 1,
													)
												}
												className="w-9 h-9 flex items-center justify-center text-muted hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
												aria-label={t('Increase quantity')}
											>
												<FontAwesomeIcon icon={faPlus} className="text-xs" />
											</button>
										</div>
									</div>
									{/* PD-14239: hide per-line price/subtotal when pricing is hidden */}
									{display.canSeePricing && (
										<div className="flex flex-col items-end">
											{(() => {
												// PD-16171 — an unpriced line is never shown as
												// $0.00; checkout is blocked until it is removed.
												if (!item.isPriced) {
													return (
														<p className="text-xs font-medium text-danger text-right">
															{t('No price — remove to check out')}
														</p>
													);
												}
												const pl = pricedLineByDetailId.get(item.id);
												const hasDiscount =
													pl != null && pl.lineDiscountTotal > 0;
												if (hasDiscount) {
													// On-sale line: strikethrough original + discounted
													// final. The math (how much was saved) is easy to read
													// from the price drop itself; the WHY (which promo
													// triggered it) shows as a chip on the title side of
													// the row, so the price column stays clean.
													return (
														<div className="flex flex-col items-end leading-tight">
															<p className="text-xs text-brand-muted line-through">
																{formatCurrency(pl.originalSubtotal.toFixed(2))}
															</p>
															<p className="text-sm font-semibold text-default">
																{formatCurrency(pl.finalSubtotal.toFixed(2))}
															</p>
														</div>
													);
												}
												return (
													<p className="text-sm font-semibold text-default">
														{formatCurrency(item.subtotal)}
													</p>
												);
											})()}
										</div>
									)}
									{/* Item-specific validation issues — inline below the item content */}
									{(hasItemErrors || hasItemWarnings) && (
										<div className="basis-full mt-1 space-y-0.5 pl-[76px]">
											{itemErrors.map((err, i) => (
												<p
													key={`e${i}`}
													className="flex items-center gap-1.5 text-xs text-danger"
												>
													<FontAwesomeIcon
														icon={faCircleExclamation}
														className="text-[10px] shrink-0"
													/>
													{t(err.message)}
												</p>
											))}
											{itemWarnings.map((warn, i) => (
												<p
													key={`w${i}`}
													className="flex items-center gap-1.5 text-xs text-warning"
												>
													<FontAwesomeIcon
														icon={faTriangleExclamation}
														className="text-[10px] shrink-0"
													/>
													{t(warn.message)}
												</p>
											))}
										</div>
									)}
								</div>
							);
						})}
					</div>

					{/* ---- FREQUENTLY BOUGHT TOGETHER (PD-13185) ---- */}
					<CartSuggestions />

					{/* ---- CHECKOUT OPTIONS ---- */}
					{checkoutLoading && (
						<div className="py-6 text-center">
							<FontAwesomeIcon
								icon={faSpinner}
								className="animate-spin text-faint"
							/>
						</div>
					)}

					{checkoutOptions != null && (
						<>
							{/* Cart-level validation errors (credit hold, min order, etc.) */}
							{cartLevelErrors.length > 0 && (
								<div className="mb-4 p-3 rounded-xl bg-danger-badge text-danger-tint-fg border border-danger-tint">
									<div className="flex items-start gap-2">
										<FontAwesomeIcon
											icon={faCircleExclamation}
											className="text-danger mt-0.5"
										/>
										<div className="text-sm">
											{cartLevelErrors.map((err, i) => (
												<p key={i} className="text-danger">
													{t(err.message)}
												</p>
											))}
										</div>
									</div>
								</div>
							)}

							{/* Cart-level validation warnings */}
							{cartLevelWarnings.length > 0 && (
								<div className="mb-4 p-3 rounded-xl bg-warning-badge text-warning-tint-fg border border-warning-tint">
									<div className="flex items-start gap-2">
										<FontAwesomeIcon
											icon={faTriangleExclamation}
											className="text-warning mt-0.5"
										/>
										<div className="text-sm">
											{cartLevelWarnings.map((warn, i) => (
												<p key={i} className="text-warning">
													{t(warn.message)}
												</p>
											))}
										</div>
									</div>
								</div>
							)}

							{/* Shipping Address */}
							<Section title={t('Shipping Address')}>
								<AddressSelector
									addresses={checkoutOptions.shippingAddresses}
									selectedId={shippingAddressId}
									onChange={handleShippingAddressChange}
								/>
							</Section>

							{/* Billing Address */}
							<Section title={t('Billing Address')}>
								<AddressSelector
									addresses={checkoutOptions.billingAddresses}
									selectedId={billingAddressId}
									onChange={handleBillingAddressChange}
								/>
							</Section>

							{/* Shipping Method */}
							{checkoutOptions.isShippingConfigured && (
								<Section title={t('Shipping Method')}>
									{checkoutOptions.hasShippingConflict ? (
										<div className="p-3 rounded-lg bg-danger-badge text-danger-tint-fg border border-danger-tint">
											<div className="flex items-start gap-2">
												<FontAwesomeIcon
													icon={faTriangleExclamation}
													className="text-danger mt-0.5"
												/>
												<p className="text-sm text-danger">
													{t(
														checkoutOptions.shippingConflictMessage ??
															'This order contains items with conflicting shipping requirements. Please split the order into separate orders.',
													)}
												</p>
											</div>
										</div>
									) : checkoutOptions.shippingMethods.length === 0 ? (
										<p className="text-sm text-brand-muted italic">
											{t('No shipping methods available')}
										</p>
									) : (
										<div className="space-y-2">
											{checkoutOptions.shippingMethods.map((method) => {
												const isSelected = shippingMethodId === method.id;
												const isDisabled = method.isDisabled;
												const displayPrice =
													computeShippingEffectivePrice(method);

												return (
													<button
														key={method.id}
														type="button"
														onClick={() => {
															if (!isDisabled) {
																setShippingMethodId(method.id);
															}
														}}
														disabled={isDisabled}
														className={`w-full text-left border rounded-lg p-3 transition-colors ${
															isDisabled
																? 'opacity-50 cursor-not-allowed border-brand-border bg-brand-input'
																: isSelected
																	? 'border-portal-accent bg-portal-accent/10 dark:bg-portal-accent/10'
																	: 'border-brand-border bg-brand-surface hover:border-strong'
														}`}
													>
														<div className="flex items-center">
															<FontAwesomeIcon
																icon={faTruck}
																className={`text-sm ${
																	isSelected
																		? 'text-portal-accent'
																		: 'text-brand-muted'
																}`}
															/>
															<div className="flex-1 ml-3 min-w-0">
																<div className="flex items-center gap-2">
																	<span
																		className={`text-sm font-medium ${
																			isSelected
																				? 'text-portal-accent'
																				: 'text-default'
																		}`}
																	>
																		{t(method.label)}
																	</span>
																	{/* PD-14239: hide shipping cost; keep the method selectable */}
																	{display.canSeePricing && (
																		<span
																			className={`text-sm font-medium ml-auto ${
																				displayPrice === '0.00'
																					? 'text-success'
																					: 'text-default'
																			}`}
																		>
																			{displayPrice === '0.00'
																				? t('Free')
																				: formatCurrency(
																						parseFloat(displayPrice),
																					)}
																		</span>
																	)}
																</div>
																{method.description != null &&
																	method.description !== '' && (
																		<p className="text-xs text-brand-muted mt-1">
																			{t(method.description)}
																		</p>
																	)}
																{
																	/* PD-14239: hide free-shipping $ threshold */ display.canSeePricing &&
																		method.freeShippingThreshold != null &&
																		displayPrice !== '0.00' && (
																			<p className="text-xs text-success mt-1">
																				{t('Free shipping on orders over')}{' '}
																				{formatCurrency(
																					parseFloat(
																						method.freeShippingThreshold,
																					),
																				)}
																			</p>
																		)
																}
																{
																	/* PD-14239: hide surcharge $ */ display.canSeePricing &&
																		method.additionalFee != null && (
																			<p className="text-xs text-warning mt-1">
																				{t('Includes')}{' '}
																				{formatCurrency(
																					parseFloat(method.additionalFee),
																				)}{' '}
																				{t('surcharge')}
																			</p>
																		)
																}
																{isDisabled &&
																	method.disabledReason != null && (
																		<p className="text-xs text-danger mt-1">
																			{t(method.disabledReason)}
																		</p>
																	)}
															</div>
														</div>
													</button>
												);
											})}
										</div>
									)}
									{checkoutOptions.shippingDisclaimer != null && (
										<p className="text-xs text-brand-muted mt-2">
											{t(checkoutOptions.shippingDisclaimer)}
										</p>
									)}
								</Section>
							)}

							{/* Payment */}
							<Section title={t('Payment')}>
								{/* PD-15084 — split tender: apply the on-account balance
								    first; the selected method collects only the remainder,
								    and no method is needed when funds cover everything. */}
								{accountFundsAvailable > 0 && (
									<div className="mb-3 pb-3 border-b border-brand-border">
										<label className="flex items-center gap-3 cursor-pointer p-2 -mx-2 rounded-lg hover:bg-brand-input">
											<input
												type="checkbox"
												checked={useAccountFunds}
												onChange={(e) => {
													setUseAccountFunds(e.target.checked);
												}}
												className="w-4 h-4 text-portal-accent"
											/>
											<span className="text-sm text-default">
												{t('Account Funds')}
											</span>
											<span className="text-xs text-brand-muted ml-auto">
												{formatCurrency(accountFundsAvailable)} {t('available')}
											</span>
										</label>
										{useAccountFunds && partialFundsBlocked && (
											<p className="ml-9 text-xs text-danger mt-1">
												{t(partialFundsMessage)}
											</p>
										)}
										{useAccountFunds && !partialFundsBlocked && (
											<p className="ml-9 text-xs text-brand-muted mt-1">
												{fundsCoverEverything
													? t(
															'Your account funds cover this order — no other payment method is needed.',
														)
													: `${t('Applies')} ${formatCurrency(Math.min(accountFundsAvailable, preFeeDisplayTotal))} ${t('now; the remaining')} ${formatCurrency(Math.max(0, Math.round((displayTotal - Math.min(accountFundsAvailable, preFeeDisplayTotal)) * 100) / 100))} ${t('is collected from the payment method selected below.')}`}
											</p>
										)}
									</div>
								)}
								<div className="space-y-2">
									{!fundsCoverEverything &&
										ecommercePaymentTypes.map((pt) => (
											<PaymentTypeOption
												key={pt.id}
												paymentType={pt}
												isSelected={paymentTypeId === pt.id}
												onSelect={() => {
													setPaymentTypeId(pt.id);
													const methodsForType =
														checkoutOptions.savedPaymentMethods.filter(
															(m) => m.type === pt.paymentMethodType,
														);
													const primary = methodsForType.find(
														(m) => m.isPrimary,
													);
													setPaymentMethodId(
														primary?.id ?? methodsForType[0]?.id ?? null,
													);
												}}
												savedMethods={checkoutOptions.savedPaymentMethods.filter(
													(m) => m.type === pt.paymentMethodType,
												)}
												selectedMethodId={paymentMethodId}
												onMethodSelect={setPaymentMethodId}
												creditInfo={checkoutOptions.credit}
												isLocked={isCycleBilling}
												cycleBillingHelper={cycleBillingHelper}
												accountFundsAvailable={
													checkoutOptions.accountFunds?.available
												}
												onAddPaymentMethod={() =>
													setShowAddPaymentSidebar(true)
												}
												onManagePaymentMethods={() => navigate('/account')}
											/>
										))}
								</div>
								{/* PD-16900 — when the card is charged. */}
								{!fundsCoverEverything && canChoosePayAtOrder && (
									<div className="mt-3 pt-3 border-t border-brand-border">
										<label className="flex items-center gap-3 cursor-pointer p-2 -mx-2 rounded-lg hover:bg-brand-input">
											<input
												type="checkbox"
												checked={payAtOrder}
												onChange={(e) => {
													setPayAtOrder(e.target.checked);
												}}
												className="w-4 h-4 text-portal-accent"
											/>
											<span className="text-sm text-default">
												{t('Pay for this order now')}
											</span>
										</label>
										<p className="ml-9 text-xs text-brand-muted mt-1">
											{payAtOrder
												? t(
														'Your card is charged for the full order when you place it. Anything not delivered is refunded, and anything extra is charged at delivery.',
													)
												: cardPaymentTiming?.mode === 'bill_on_terms'
													? t(
															'Otherwise this order is billed on your account terms.',
														)
													: t(
															'Otherwise your card is charged when the order is delivered, for what is delivered.',
														)}
										</p>
									</div>
								)}
								{!fundsCoverEverything && accountPaysAtOrder && (
									<p className="mt-3 text-xs text-brand-muted">
										{t(
											'Your card is charged for the full order when you place it. Anything not delivered is refunded, and anything extra is charged at delivery.',
										)}
									</p>
								)}
							</Section>

							{/* Customer PO */}
							<Section title={t('Customer PO')}>
								<input
									type="text"
									value={customerPO}
									onChange={(e) => {
										setCustomerPO(e.target.value);
										// PD-16936 — a refused submit's PO error is about the
										// text being edited now; the counter takes over.
										setValidationErrors((issues) =>
											issues.some((issue) => issue.type === 'customerPoTooLong')
												? issues.filter(
														(issue) => issue.type !== 'customerPoTooLong',
													)
												: issues,
										);
									}}
									placeholder={t('Optional')}
									aria-invalid={customerPoError != null}
									aria-describedby={
										customerPoMaxLength != null ? 'customer-po-limit' : undefined
									}
									className={`w-full px-3 py-2.5 rounded-lg bg-brand-input border text-sm text-default ${
										customerPoError != null ? 'border-danger' : 'border-brand-border'
									}`}
								/>
								{/* PD-16936 — the tenant's PO limit. Everything typed or
								    pasted stays in the box; Place Order waits until the
								    buyer shortens it. */}
								{customerPoMaxLength != null && (
									<div id="customer-po-limit">
										<p
											className={`mt-1 text-xs ${
												customerPoError != null ? 'text-danger' : 'text-brand-muted'
											}`}
										>
											{t('{{count}} of {{max}} characters', {
												count: customerPoLength(trimmedCustomerPO),
												max: customerPoMaxLength,
											})}
										</p>
										{customerPoError != null && (
											<p className="mt-1 text-sm text-danger" role="alert">
												{customerPoError}
											</p>
										)}
									</div>
								)}
							</Section>

							{/* Minimum order warning */}
							{
								/* PD-14239: hide minimum-order $ warning */ display.canSeePricing &&
									checkoutOptions.minimumOrderAmount != null &&
									parseFloat(cart?.subtotal ?? '0') <
										checkoutOptions.minimumOrderAmount && (
										<div className="mb-4 p-3 rounded-xl text-sm bg-warning-badge text-warning-tint-fg border border-warning-tint">
											<div className="flex items-center gap-2">
												<FontAwesomeIcon
													icon={faTriangleExclamation}
													className="text-warning"
												/>
												<span className="text-warning">
													{t('Minimum order amount')}:{' '}
													{formatCurrency(checkoutOptions.minimumOrderAmount)}
												</span>
											</div>
										</div>
									)
							}
						</>
					)}

					{/* ---- COUPONS + ORDER SUMMARY ----
			    The Order Summary card now lives in the sticky right sidebar (opened
			    below). On mobile the layout is a single column: the full summary
			    stacks inline here at the foot and a slim pinned bar carries the
			    Place-Order CTA. Bottom-anchored sticky is deliberately avoided on
			    mobile — Android keyboards shrink visualViewport.height while fixed
			    elements stay pinned to innerHeight, which would occlude the CTA. */}
					{/* Coupon entry + applied codes + almost-qualified hints —
				    rendered above the totals card so the customer's eye flows
				    "what's in cart → savings/qualifier opportunities → totals". */}
					<CartPromoPanel
						appliedCouponCodes={cart?.appliedCouponCodes ?? []}
						priced={cart?.priced ?? null}
						onCartUpdate={setCart}
						token={token}
					/>
					{/* /LEFT COLUMN */}
				</div>

				{/* RIGHT SIDEBAR (md+) / inline at foot (mobile): Order Summary card.
			    The right grid item stretches; the card inside is md:sticky below the
			    sticky PortalHeader (top-16 clears its ~56px height). On mobile it just
			    stacks at the foot — its Place-Order button is hidden there because the
			    slim pinned bar below owns the mobile CTA. */}
				<div>
					<div className="bg-brand-surface border border-brand-border rounded-xl p-4 md:sticky md:top-16">
						{/* PD-14239: hide the entire money summary (subtotal, shipping, tax, fees, savings, total) when pricing is hidden */}
						{display.canSeePricing && (
							<>
								<h2 className="text-sm font-semibold text-default mb-3">
									{t('Order Summary')}
								</h2>
								<div className="space-y-1 mb-4 text-sm">
									<SummaryRow
										label={`${t('Items')} (${lineItems.length})`}
										value={formatCurrency(cart?.subtotal)}
									/>
									{shippingFeePreview > 0 && (
										<SummaryRow
											label={t('Shipping')}
											value={formatCurrency(shippingFeePreview)}
										/>
									)}
									{cart?.tax != null && parseFloat(cart.tax) > 0 && (
										<SummaryRow
											label={t('Tax')}
											value={formatCurrency(cart.tax)}
										/>
									)}
									{feePreview > 0 && (
										<SummaryRow
											label={t('Processing Fee')}
											value={formatCurrency(feePreview)}
										/>
									)}
									{promoSavings > 0 &&
										(() => {
											const promos = cart?.priced?.appliedPromos ?? [];
											// Single promo: just show the named promo line — the
											// "Savings" parent row would just repeat the same number,
											// which reads as duplicated. Multiple promos: show the
											// aggregate Savings total + indented per-promo breakdown.
											if (promos.length === 1) {
												const p = promos[0];
												return (
													<div className="flex items-center justify-between text-success">
														<span className="font-medium truncate">
															{t(p.promoName)}
															{p.couponCodeUsed != null && (
																<span className="ml-1 text-brand-muted text-xs">
																	({t(p.couponCodeUsed)})
																</span>
															)}
														</span>
														<span>
															−{formatCurrency(p.totalDiscountAmount)}
														</span>
													</div>
												);
											}
											return (
												<>
													<div className="flex items-center justify-between text-success">
														<span className="font-medium">{t('Savings')}</span>
														<span>−{formatCurrency(promoSavings)}</span>
													</div>
													{promos.map((promo) => (
														<div
															key={promo.promoId}
															className="flex items-center justify-between text-xs text-success pl-2"
														>
															<span className="truncate">
																{t(promo.promoName)}
																{promo.couponCodeUsed != null && (
																	<span className="ml-1 text-brand-muted">
																		({t(promo.couponCodeUsed)})
																	</span>
																)}
															</span>
															<span>
																−{formatCurrency(promo.totalDiscountAmount)}
															</span>
														</div>
													))}
												</>
											);
										})()}
									<div className="flex items-center justify-between font-semibold text-default pt-2 border-t border-subtle">
										<span>{t('Total')}</span>
										<span>{formatCurrency(displayTotal)}</span>
									</div>
								</div>
							</>
						)}

						{submitError != null && (
							<p className="text-sm text-danger mb-3">{t(submitError)}</p>
						)}

						{/* PD-13362 — why Place Order is disabled for a lead. Desktop only
						    here; the mobile pinned bar carries its own copy. */}
						{display.isAccountPending && (
							<div className="hidden md:block">
								<AccountPendingNotice />
							</div>
						)}

						{/* Desktop CTA lives in the sticky sidebar card; on mobile the pinned
				    bar below owns it, so this button is hidden under md. Over the
				    buyer's approval limit it asks for approval instead (PD-16845). */}
						{needsApproval ? (
							<button
								type="button"
								onClick={() => void handleRequestApproval()}
								disabled={!canRequestApproval}
								className="w-full hidden md:flex items-center justify-center gap-2 px-4 py-3 bg-portal-accent enabled:hover:bg-portal-accent-hover disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors min-h-touch"
							>
								{(requestingApproval || hasPendingUpdates) && (
									<FontAwesomeIcon icon={faSpinner} className="animate-spin" />
								)}
								{hasPendingUpdates
									? t('Updating cart...')
									: requestApprovalLabel}
							</button>
						) : (
							<button
								type="button"
								onClick={handleSubmit}
								disabled={
									!canSubmit ||
									submitting ||
									checkoutOptions == null ||
									hasPendingUpdates
								}
								className="w-full hidden md:flex items-center justify-center gap-2 px-4 py-3 bg-portal-accent enabled:hover:bg-portal-accent-hover disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors min-h-touch"
							>
								{(submitting || hasPendingUpdates) && (
									<FontAwesomeIcon icon={faSpinner} className="animate-spin" />
								)}
								{hasPendingUpdates
									? t('Updating cart...')
									: submitting
										? t('Placing Order...')
										: t('Place Order')}
							</button>
						)}
					</div>
					{/* /RIGHT SIDEBAR */}
				</div>
				{/* /md grid */}
			</div>

			{/* MOBILE PINNED PLACE-ORDER BAR — only below md (where the BottomNav
			    lives). Sits flush on top of the BottomNav: bottom = the nav's height
			    (its TabButtons are min-h-[56px]) + the iOS safe-area inset the nav
			    pads itself with (pb-safe-bottom), so there's no gap between this bar
			    and the footer on any device. z-20 keeps it under the nav's z-30.
			    Hidden while the soft keyboard is up (mirrors BottomNav) so it can't be
			    occluded. Reuses the same submit handler + disabled logic as the desktop
			    card button; qty + total are baked into the label like the header. */}
			{!keyboardOpen && (
				<div className="md:hidden fixed bottom-[calc(56px_+_env(safe-area-inset-bottom))] left-0 right-0 z-20 bg-brand-surface border-t border-brand-border px-4 py-2">
					{/* PD-13362 — why Place Order is disabled for a lead. */}
					{display.isAccountPending && <AccountPendingNotice compact />}
					{needsApproval ? (
						<button
							type="button"
							onClick={() => void handleRequestApproval()}
							disabled={!canRequestApproval}
							className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-portal-accent enabled:hover:bg-portal-accent-hover disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors min-h-touch"
						>
							{(requestingApproval || hasPendingUpdates) && (
								<FontAwesomeIcon icon={faSpinner} className="animate-spin" />
							)}
							{hasPendingUpdates
								? t('Updating cart...')
								: requestingApproval
									? requestApprovalLabel
									: `${requestApprovalLabel} · ${totalQuantity} ${
											totalQuantity === 1 ? t('item') : t('items')
										}${display.canSeePricing ? ` · ${formatCurrency(displayTotal)}` : ''}`}
						</button>
					) : (
						<button
							type="button"
							onClick={handleSubmit}
							disabled={
								!canSubmit ||
								submitting ||
								checkoutOptions == null ||
								hasPendingUpdates
							}
							className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-portal-accent enabled:hover:bg-portal-accent-hover disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors min-h-touch"
						>
							{(submitting || hasPendingUpdates) && (
								<FontAwesomeIcon icon={faSpinner} className="animate-spin" />
							)}
							{hasPendingUpdates
								? t('Updating cart...')
								: submitting
									? t('Placing Order...')
									: `${t('Place Order')} · ${totalQuantity} ${
											totalQuantity === 1 ? t('item') : t('items')
										}${display.canSeePricing ? ` · ${formatCurrency(displayTotal)}` : ''}`}
						</button>
					)}
				</div>
			)}

			{/* Clear-cart confirmation */}
			{/* PD-16731 — likely duplicate: the cart matches an order just placed. */}
			<ConfirmDialog
				isOpen={recentMatchPrompt != null}
				title={t('Place this order again?')}
				message={
					recentMatchPrompt == null
						? ''
						: t(
								'Order #{{number}} with the same items and quantities was placed at {{time}}. Place this order again?',
								{
									number:
										displayExternalId({
											id: recentMatchPrompt.match.orderId,
											externalId: recentMatchPrompt.match.externalId,
											externalId2: recentMatchPrompt.match.externalId2,
										}) ?? String(recentMatchPrompt.match.orderId),
									time: formatDateTime(recentMatchPrompt.match.date),
								},
							)
				}
				confirmText={t('Place Order')}
				cancelText={t('Cancel')}
				variant="warning"
				onConfirm={() => {
					recentMatchPrompt?.resolve(true);
					setRecentMatchPrompt(null);
				}}
				onCancel={() => {
					recentMatchPrompt?.resolve(false);
					setRecentMatchPrompt(null);
				}}
			/>

			<ConfirmDialog
				isOpen={showClearConfirm}
				title={t('Clear cart?')}
				message={t(
					`This will remove all ${lineItems.length} item(s) from your cart. This action cannot be undone.`,
				)}
				confirmText={clearing ? t('Clearing...') : t('Clear Cart')}
				cancelText={t('Cancel')}
				variant="danger"
				onCancel={() => {
					if (!clearing) setShowClearConfirm(false);
				}}
				onConfirm={() => void handleClearCart()}
			/>

			{/* Add Payment Method sidebar */}
			<PortalSidebar
				isOpen={showAddPaymentSidebar}
				onClose={() => setShowAddPaymentSidebar(false)}
				title={t('Add Payment Method')}
				width="md"
				position="right"
				modal={true}
			>
				<div className="p-4">
					<PortalAddPaymentMethod
						onSuccess={() => void handlePaymentMethodAdded()}
						onCancel={() => setShowAddPaymentSidebar(false)}
						supportedPaymentMethods={checkoutOptions?.paymentTypes}
						preSelectedPaymentType={
							selectedPaymentType?.paymentMethodType === 'ACH'
								? PaymentMethodTypeEnum.ACH
								: selectedPaymentType?.paymentMethodType === 'Credit Card'
									? PaymentMethodTypeEnum.CREDIT_CARD
									: undefined
						}
					/>
				</div>
			</PortalSidebar>

			{/* PD-15185 — a refused payment, acknowledged deliberately. */}
			<PaymentDeclineModal
				decline={decline}
				onClose={(): void => {
					setDecline(null);
				}}
			/>
		</div>
	);
}

// --- Sub-components ---

function Section({
	title,
	children,
}: {
	title: string;
	children: React.ReactNode;
}): JSX.Element {
	return (
		<div className="mb-4">
			<h2 className="text-xs font-semibold text-brand-muted uppercase tracking-wide mb-2">
				{title}
			</h2>
			<div className="bg-brand-surface border border-brand-border rounded-xl p-4">
				{children}
			</div>
		</div>
	);
}

function AddressSelector({
	addresses,
	selectedId,
	onChange,
}: {
	addresses: Array<PortalCheckoutAddressDto>;
	selectedId: number | null;
	onChange: (id: number) => void;
}): JSX.Element {
	const { t } = useTranslation();

	if (addresses.length === 0) {
		return (
			<p className="text-sm text-brand-muted">{t('No addresses available')}</p>
		);
	}

	return (
		<select
			value={selectedId ?? ''}
			onChange={(e) => onChange(parseInt(e.target.value, 10))}
			className="w-full px-3 py-2.5 rounded-lg bg-brand-input border border-brand-border text-sm text-default"
		>
			{addresses.map((addr) => (
				<option key={addr.id} value={addr.id}>
					{t(
						[addr.address1, addr.city, addr.state, addr.zip]
							.filter(Boolean)
							.join(', '),
					)}
				</option>
			))}
		</select>
	);
}

function PaymentTypeOption({
	paymentType,
	isSelected,
	onSelect,
	savedMethods,
	selectedMethodId,
	onMethodSelect,
	creditInfo,
	accountFundsAvailable,
	onAddPaymentMethod,
	onManagePaymentMethods,
	isLocked = false,
	cycleBillingHelper = null,
}: {
	paymentType: PortalCheckoutPaymentTypeDto;
	isSelected: boolean;
	onSelect: () => void;
	savedMethods: Array<PortalCheckoutPaymentMethodDto>;
	selectedMethodId: number | null;
	onMethodSelect: (id: number) => void;
	creditInfo?: PortalClientCreditDto | null;
	/**
	 * PD-15869 — cycle billing decides the payment for the buyer; the radio
	 * is checked and read-only rather than hidden, so the page still says
	 * plainly how the order will be paid.
	 */
	isLocked?: boolean;
	/** PD-15869 — replaces the net-terms helper under cycle billing. */
	cycleBillingHelper?: string | null;
	// PD-13704: available on-account funds, shown next to the Account Funds option.
	accountFundsAvailable?: number | null;
	onAddPaymentMethod: () => void;
	onManagePaymentMethods: () => void;
}): JSX.Element {
	const { t } = useTranslation();
	// PD-14239: gate this card's money rows on the buyer's pricing visibility
	const display = useDisplaySettings();

	// PD-15098 — "Company Credit" was previously labeled "Pay on Account",
	// which properly means an unallocated deposit onto the account. Customers
	// read it that way at checkout and reported the balance not moving and the
	// "missing" transaction — both correct behavior for a net-terms order.
	const typeLabels: Record<string, string> = {
		'Credit Card': 'Credit Card',
		ACH: 'ACH / Bank Transfer',
		'Company Credit': 'On Account (Net Terms)',
		Cash: 'Cash',
		Check: 'Check',
	};

	const label =
		typeLabels[paymentType.paymentMethodType] ?? paymentType.paymentMethodType;
	// PD-15142 — the rate configured against a payment-method TYPE is what that
	// type charges in general; whether the buyer actually pays it depends on the
	// specific method they pick. A card that cannot be surcharged pays nothing,
	// so the row must say so rather than keep advertising the type's rate.
	// Resolved server-side, so this stays true for any future method type with
	// its own eligibility rules rather than being a card-specific special case.
	const feeSuppressedForSelectedMethod = savedMethods.some(
		(method) => method.id === selectedMethodId && !method.isSurchargeable,
	);
	const effectiveFeeForDisplay = feeSuppressedForSelectedMethod
		? 0
		: (paymentType.processingFee ?? 0);

	const showMethodSelector =
		isSelected &&
		['Credit Card', 'ACH'].includes(paymentType.paymentMethodType) &&
		savedMethods.length > 0;

	return (
		<div>
			<label className="flex items-center gap-3 cursor-pointer p-2 -mx-2 rounded-lg hover:bg-brand-input">
				<input
					type="radio"
					name="paymentType"
					checked={isSelected}
					onChange={onSelect}
					disabled={isLocked}
					className="w-4 h-4 text-portal-accent"
				/>
				<FontAwesomeIcon icon={faCreditCard} className="text-faint" />
				<span className="text-sm text-default">{t(label)}</span>
				{
					/* PD-14239: processingFee is null for hide-pricing buyers, who
					   see nothing here at all.
					   PD-15142: every other buyer sees where they stand on every
					   method — the fee where one applies, and "No fee" where none
					   does. Silence read as "no fee" only by inference, which was
					   wrong for a method whose fee simply had not loaded, and is
					   now wrong in the other direction too: a card that cannot be
					   surcharged pays nothing, and saying so beats leaving a gap
					   where the rate used to be. */ display.canSeePricing &&
						paymentType.processingFee != null && (
							<span className="text-xs text-faint ml-auto">
								{effectiveFeeForDisplay > 0
									? paymentType.processingFeeType === '%'
										? `${effectiveFeeForDisplay}% ${t('fee')}`
										: `${formatCurrency(effectiveFeeForDisplay)} ${t('fee')}`
									: t('No fee')}
							</span>
						)
				}
				{/* PD-13704: show the customer's available on-account balance next to
				    the Account Funds option so they can see what they have to spend. */}
				{
					/* PD-14239: hide available account funds */ display.canSeePricing &&
						paymentType.paymentMethodType === ACCOUNT_FUNDS_TYPE &&
						accountFundsAvailable != null && (
							<span className="text-xs text-brand-muted ml-auto">
								{formatCurrency(accountFundsAvailable)} {t('available')}
							</span>
						)
				}
			</label>
			{showMethodSelector && (
				<div className="ml-9 mt-1 mb-2">
					{/* PD-15878 — the shared Select, so each option carries its network's
				    mark. PD-15154's funding type stays on every option: the row
				    above suppresses the fee for a card that cannot carry one, and
				    without it the rate disappears with nothing saying why.
				    PD-16381 — the same two-line option as every other payment
				    picker: the method on the first line, its funding and fee
				    smaller on the second. The closed control shows the method
				    alone, since the row above already states the fee it will pay. */}
					<Select
						aria-label={t('Payment Method')}
						value={selectedMethodId ?? ''}
						onChange={(next) => {
							if (next !== '') {
								onMethodSelect(parseInt(next, 10));
							}
						}}
						options={savedMethods.map((m) => {
							// Priced by this row's type, with no rate read as none —
							// the same figure the row itself shows.
							const option = formatPaymentMethodOptionLabel(m, t, {
								feeTypes: [
									{
										paymentMethodType: paymentType.paymentMethodType,
										processingFeeType: paymentType.processingFeeType,
										processingFee: paymentType.processingFee ?? 0,
									},
								],
							});
							return {
								value: m.id,
								label: option.primary,
								subtitle: option.secondary,
								icon: (
									<CardBrandIcon
										brand={m.subtype ?? m.type}
										className="text-xl"
									/>
								),
							};
						})}
					/>
				</div>
			)}
			{isSelected &&
				['Credit Card', 'ACH'].includes(paymentType.paymentMethodType) && (
					<div className="ml-9 mt-1 mb-2 flex items-center gap-3">
						{savedMethods.length === 0 && (
							<span className="text-xs text-brand-muted">
								{t('No saved payment methods.')}
							</span>
						)}
						<button
							type="button"
							onClick={onAddPaymentMethod}
							className="text-xs font-medium text-portal-accent hover:underline"
						>
							{t('+ Add new')}
						</button>
						<button
							type="button"
							onClick={onManagePaymentMethods}
							className="text-xs text-brand-muted hover:underline"
						>
							{t('Manage')}
						</button>
					</div>
				)}
			{/* PD-15098: one line on what ordering on terms means, so the
			    semantics are explicit — no money moves at checkout.
			    PD-15869: under cycle billing that line is wrong — the invoice
			    is not paid when it ships, it joins the account's next cycle
			    charge — so the cycle line replaces it. */}
			{isSelected && paymentType.paymentMethodType === 'Company Credit' && (
				<p className="ml-9 mt-1 mb-2 text-xs text-brand-muted">
					{cycleBillingHelper ??
						t(
							'Order on your credit line — pay the invoice after your order ships.',
						)}
				</p>
			)}
			{/* Credit info nested under On Account (Net Terms) when selected */}
			{
				/* PD-14239: hide on-account credit figures */ display.canSeePricing &&
				isSelected === true &&
				paymentType.paymentMethodType === 'Company Credit' &&
				creditInfo != null &&
				creditInfo.creditLimit != null ? (
					<div className="ml-9 mt-1 mb-2 p-3 bg-portal-accent/10 dark:bg-portal-accent/10 border border-portal-accent/30 rounded-lg text-sm">
						<div className="flex justify-between text-brand-muted">
							<span>{t('Credit Limit')}</span>
							<span>{formatCurrency(creditInfo.creditLimit)}</span>
						</div>
						<div className="flex justify-between text-brand-muted">
							<span>{t('Current Balance')}</span>
							<span>{formatCurrency(creditInfo.creditBalance)}</span>
						</div>
						<div className="flex justify-between font-medium text-default pt-1 border-t border-portal-accent/20 mt-1">
							<span>{t('Available Credit')}</span>
							<span>{formatCurrency(creditInfo.availableCredit)}</span>
						</div>
						{creditInfo.hasCreditHold === true && (
							<p className="text-danger mt-2 font-medium">
								{t('Account is on credit hold')}
							</p>
						)}
					</div>
				) : null
			}
		</div>
	);
}

function SummaryRow({
	label,
	value,
}: {
	label: string;
	value: string | null;
}): JSX.Element {
	return (
		<div className="flex items-center justify-between text-brand-muted">
			<span>{label}</span>
			<span>{value ?? '$0.00'}</span>
		</div>
	);
}

/**
 * Cart promo panel — coupon entry + currently-applied codes + "almost
 * qualified" hints. Sits above the order summary so the customer sees
 * opportunities for additional savings before they place the order.
 *
 * Submitting a coupon hits POST /api/portal/cart/coupons (rate-limited
 * server-side at 10/min and 60/hour per user). The cart response that
 * comes back already includes the re-evaluated `priced` block so the
 * order summary updates in the same render.
 */
function CartPromoPanel({
	appliedCouponCodes,
	priced,
	onCartUpdate,
	token,
}: {
	appliedCouponCodes: Array<string>;
	priced: PricedCart | null;
	onCartUpdate: (cart: PortalCartDto | null) => void;
	token: string | null;
}): JSX.Element | null {
	const { t } = useTranslation();
	// PD-14239: gate this card's money rows on the buyer's pricing visibility
	const display = useDisplaySettings();
	const [code, setCode] = useState('');
	const [submitting, setSubmitting] = useState(false);
	const [removingCode, setRemovingCode] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);

	const unfulfilled = priced?.unfulfilledPromos ?? [];
	const earnedGifts = priced?.unconfiguredFreeGifts ?? [];
	const [addingGiftItemId, setAddingGiftItemId] = useState<number | null>(null);

	// Keep the panel out of the DOM entirely when there's nothing to show
	// AND no auth token (anonymous browsers can't have coupons / hints).
	if (
		token == null &&
		appliedCouponCodes.length === 0 &&
		unfulfilled.length === 0
	) {
		return null;
	}

	// PD-14375 — one-click add for an earned BXGY reward. The engine
	// discounts the reward line once it's in the cart; nothing is ever
	// added automatically.
	const addGiftToCart = async (
		itemId: number,
		quantity: number,
	): Promise<void> => {
		if (token == null) {
			return;
		}
		setError(null);
		setAddingGiftItemId(itemId);
		try {
			const res = await cartService.addToCart(token, { itemId, quantity });
			if (res.success) {
				onCartUpdate(res.data);
			}
		} catch {
			setError(t('Could not add that item. Try again.'));
		} finally {
			setAddingGiftItemId(null);
		}
	};

	const apply = async (raw: string) => {
		const trimmed = raw.trim();
		if (trimmed.length === 0 || token == null) {
			return;
		}
		setError(null);
		setSubmitting(true);
		try {
			const res = await cartService.applyCartCoupon(token, trimmed);
			if (res.success) {
				onCartUpdate(res.data);
				setCode('');
			} else {
				setError(t('Could not apply that code. Try again.'));
			}
		} catch (err) {
			// Rate-limit / 4xx / network — surface a friendly message.
			const message =
				err instanceof Error && err.message.length > 0
					? err.message
					: t('Could not apply that code. Try again.');
			setError(message);
		} finally {
			setSubmitting(false);
		}
	};

	const remove = async (codeToRemove: string) => {
		if (token == null) return;
		setError(null);
		setRemovingCode(codeToRemove);
		try {
			const res = await cartService.removeCartCoupon(token, codeToRemove);
			if (res.success) {
				onCartUpdate(res.data);
			}
		} catch {
			setError(t('Could not remove that code. Try again.'));
		} finally {
			setRemovingCode(null);
		}
	};

	return (
		<div className="bg-brand-surface border border-brand-border rounded-xl p-4 mb-4 space-y-3">
			{/* PD-14375 — earned-but-unclaimed BXGY reward. The customer has
			    met the buy requirement and the reward item is not in the
			    cart yet: prompt prominently, never auto-add. */}
			{token != null && earnedGifts.length > 0 && (
				<div className="space-y-1.5">
					{earnedGifts.map((gift) => (
						<div
							key={gift.promoId}
							className="rounded-lg px-3 py-2 bg-success-badge text-success-tint-fg border border-success-tint"
						>
							<div className="flex items-center gap-2 text-xs font-semibold">
								<FontAwesomeIcon icon={faTag} className="shrink-0" />
								<span>
									{gift.getDiscountType === 'FREE'
										? t("You've earned a free item!")
										: t("You've earned a reward!")}
								</span>
							</div>
							{gift.options.map((option) => (
								<div
									key={option.itemId}
									className="mt-1.5 flex items-center justify-between gap-2 text-xs"
								>
									<span>
										{t(option.itemDescription ?? `Item #${option.itemId}`)}
										{' — '}
										{gift.getDiscountType === 'FREE'
											? `${gift.getQty} ${t('free with')} ${t(gift.promoName)}`
											: gift.getDiscountType === 'PERCENT'
												? `${gift.getQty} ${t('at')} ${gift.getDiscountValue ?? 0}% ${t('off with')} ${t(gift.promoName)}`
												: `${gift.getQty} ${t('at')} $${gift.getDiscountValue ?? 0} ${t('off with')} ${t(gift.promoName)}`}
									</span>
									<button
										type="button"
										onClick={() => {
											void addGiftToCart(option.itemId, gift.getQty);
										}}
										disabled={addingGiftItemId != null}
										className="shrink-0 rounded-md px-2.5 py-1 text-xs font-semibold bg-portal-accent text-white disabled:opacity-50"
									>
										{addingGiftItemId === option.itemId
											? t('Adding…')
											: t('Add to cart')}
									</button>
								</div>
							))}
						</div>
					))}
				</div>
			)}

			{/* Almost-qualified hints — surfaced first so customers see the
			    "spend $X more" / "add Y more" before the coupon entry. */}
			{
				/* PD-14239: hide "spend $X more" savings hints */ display.canSeePricing &&
					unfulfilled.length > 0 && (
						<div className="space-y-1.5">
							{unfulfilled.map((u) => (
								<div
									key={u.promoId}
									className="flex items-start gap-2 text-xs rounded-lg px-2.5 py-1.5 bg-warning-badge text-warning-tint-fg border border-warning-tint"
								>
									<FontAwesomeIcon
										icon={faTriangleExclamation}
										className="mt-0.5 text-warning shrink-0"
									/>
									<span>{t(u.hint)}</span>
								</div>
							))}
						</div>
					)
			}

			{/* Coupon entry — only shown to authenticated visitors (token != null). */}
			{token != null && (
				<>
					<div className="flex items-center gap-2 text-xs font-semibold text-strong uppercase tracking-wide">
						<FontAwesomeIcon icon={faTag} className="text-portal-accent" />
						{t('Promo Code')}
					</div>
					<form
						onSubmit={(e) => {
							e.preventDefault();
							void apply(code);
						}}
						className="flex items-center gap-2"
					>
						<input
							type="text"
							value={code}
							onChange={(e) => setCode(e.target.value)}
							placeholder={t('Enter code')}
							disabled={submitting}
							className="flex-1 min-w-0 px-3 py-2 text-sm rounded-lg border border-brand-border bg-brand-input text-default placeholder:text-brand-muted disabled:opacity-60"
							autoComplete="off"
							spellCheck={false}
						/>
						<button
							type="submit"
							disabled={submitting || code.trim().length === 0}
							className="px-3 py-2 text-sm font-medium bg-portal-accent enabled:hover:bg-portal-accent-hover disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg min-h-touch"
						>
							{submitting ? (
								<FontAwesomeIcon icon={faSpinner} className="animate-spin" />
							) : (
								t('Apply')
							)}
						</button>
					</form>
					{error != null && <p className="text-xs text-danger">{t(error)}</p>}

					{appliedCouponCodes.length > 0 && (
						<div className="flex flex-wrap gap-1.5">
							{appliedCouponCodes.map((c) => (
								<span
									key={c}
									className="inline-flex items-center gap-1.5 text-xs font-medium rounded-full pl-2.5 pr-1.5 py-1 bg-success-badge text-success-tint-fg border border-success-tint"
								>
									{t(c)}
									<button
										type="button"
										onClick={() => void remove(c)}
										disabled={removingCode === c}
										className="w-4 h-4 inline-flex items-center justify-center rounded-full hover:bg-success-tint disabled:opacity-60"
										aria-label={t('Remove coupon')}
									>
										<FontAwesomeIcon
											icon={removingCode === c ? faSpinner : faTimes}
											spin={removingCode === c}
											className="text-[9px]"
										/>
									</button>
								</span>
							))}
						</div>
					)}
				</>
			)}
		</div>
	);
}
